import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { LearnersList } from "./components/learners";
import { EditSimulationReportsDetail } from "./components/detail";
import { UserGroupsFilter } from "../../courses/edit/components/learners/filters/UserGroupsFilter";
import { fetchCourseLearners, fetchLearningRecordSimulation, fetchTranscriptsForLearningSimulation } from "src/redux/actions/coaching/learners";
import { fetchDraftSimulation, fetchTranscriptsForSimulation } from "src/redux/actions/coaching/simulation";

import { Simulation } from "src/types/coaching/simulation";
import { FetchCourseLearnersRequest } from "src/types/coaching/learners";
import { UserGroup } from "src/types/users";
import InfiniteScroll from "src/components/infiniteScroll";
import { SimulationReportContext, SimulationReportContextProps } from "./context";
import { CallRecording } from "./components/recordings";

import _ from "lodash";
import cx from "classnames";
import styles from "./reports.module.scss";
import userRoleService from "src/utils/userRoles.service";
import {
  COACHING_REPORTS_PATH,
  buildManageNavigationState,
  getManageNavigationFrom,
  getManageNavigationSelectedUserGroups,
  getManageReportsBackLabel,
  navigateManageLearnerReportsBack,
} from "src/pages/coaching/utils/manageNavigation";

export const EditSimulationsReportsPage: React.FC<object> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id as string;
  const learningId = params.learningId as string;
  const navigationFrom = getManageNavigationFrom(location.state);

  const [selectedUserGroups, setSelectedUserGroups] = useState<UserGroup[]>(
    () => getManageNavigationSelectedUserGroups(location.state) ?? [],
  );

  const loading = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.attemptLearners.loading`);
  });

  const [contextState, setContextState] = useState<SimulationReportContextProps>({
    showCallRecording: false,
    toggleCallRecording: () => {}
  });

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      toggleCallRecording: () => {
        setContextState(prevState => ({
          ...prevState,
          showCallRecording: !prevState.showCallRecording,
        }));
      },
    }));
  }, []);

  const simulation: Simulation = useSelector(
    (state: any) =>
      _.get(state.coaching, `simulations.simulation[${id}].item`) as Simulation,
  );
  
  const simulationLoading = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation[${id}].loading`);
  });

  const {
    learnersLoading,
    learnersLoaded,
    learners,
    totalRecordsCount,
    currentPage,
    nextPage,
    hasMoreItems,
  } = useSelector((state: any) => {
    const learnersState = _.get(state.courses, "learners") || {};
    const loading = _.get(learnersState, "loading");
    const loaded = _.get(learnersState, "loaded");
    const pagination = _.get(learnersState, "pagination");
    const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const learners = _.get(learnersState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      learnersLoading: loading,
      learnersLoaded: loaded,
      learners: learners,
      totalRecordsCount: totalRecordsCount,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
    };
  }, _.isEqual);


  const learner = learners.find((item: any) => item.id === learningId);

  const showReportsEmptyState =
    learnersLoaded && !learnersLoading && learners.length === 0;

  const reportsEmptyDescription = useMemo(() => {
     if (_.size(selectedUserGroups) > 0) {
       const count = _.size(selectedUserGroups);
       const groupNames = selectedUserGroups.map((group) => group.name).join(", ");

       return `The selected user ${count > 1 ? 'groups' : 'group'}, ${groupNames}, ${count > 1 ? "don't" : "doesn't"} contain any learners. Choose a different group to view reports`;
     }

     return "No completed learners match the current filters. Try choosing another group.";
   }, [selectedUserGroups]);

  const [selectedAttempt, setSelectedAttempt] = useState<any>(learner);

  const fetchingTranscripts = useSelector((state: any) => {
    const fetching = _.get(state.coaching, `simulations.transcripts.[${selectedAttempt?.id}].fetching`);
    return fetching;
  });
  
  const transcripts = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.transcripts.[${selectedAttempt?.id}].item`);
  });

  const learningRecord = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.learnerReport.item`);
  });

  const buildRequest = useCallback(
    (page: number): FetchCourseLearnersRequest => ({
      course: id,
      status: "COMPLETED",
      page,
      limit: 10,
      userGroups: selectedUserGroups.length
        ? selectedUserGroups.map((group) => group.id)
        : undefined,
    }),
    [id, selectedUserGroups],
  );

  const fetchLearners = useCallback(
    (page: number) => {
      if (id) {
        dispatch(fetchCourseLearners(buildRequest(page)));
      }
    },
    [buildRequest, dispatch, id],
  );

  const handleLoadMoreLearners = () => {
    if (nextPage !== null) {
      fetchLearners(nextPage);
    }
  };

  const handleUserGroupsChange = (groups: UserGroup[]) => {
    setSelectedUserGroups(groups);
  };

  useEffect(() => {
    if (id && !simulation) {
      dispatch(fetchDraftSimulation(id));
    }
  }, [dispatch, id, simulation]);

  useEffect(() => {
    fetchLearners(0);
  }, [fetchLearners]);

  useEffect(() => {
    if (learnersLoading || !learnersLoaded) {
      return;
    }

    if (learners.length === 0) {
      if (learningId && id) {
        navigate(`/coaching/manage/simulations/${id}/reports`, {
          state: {
            ...(location.state as object),
            ...(selectedUserGroups.length ? { selectedUserGroups } : {}),
          },
          replace: true,
        });
      }
      return;
    }

    const currentInList = learners.some((item: { id: string }) => item.id === learningId);

    if (currentInList) {
      return;
    }

    navigate(`/coaching/manage/simulations/${id}/reports/${learners[0].id}`, {
      state: {
        ...(location.state as object),
        ...(selectedUserGroups.length ? { selectedUserGroups } : {}),
      },
    });
  }, [
    id,
    learningId,
    learners,
    learnersLoaded,
    learnersLoading,
    navigate,
    navigationFrom,
    selectedUserGroups,
  ]);


  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        simulation: simulation
      }
    });
  }, [simulation]);

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      learners,
    }));
  }, [learners]);

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      fetchTranscripts: handleFetchTranscripts,
    }));
  }, [learner]);

  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        fetchingTranscripts: fetchingTranscripts,
        transcripts: transcripts
      }
    });
  }, [fetchingTranscripts, transcripts]);

  useEffect(() => {
    setContextState((prev) => {
      return {
        ...prev,
        learningRecord: learningRecord,
      }
    });
  }, [learningRecord]);

  const handleFetchTranscripts = (attempt?: any) => {
    setSelectedAttempt(attempt)
    const attemptId = attempt?.id || learner?.id; 
    if (!attemptId) return;

    dispatch(fetchTranscriptsForSimulation(attemptId));
  }

  const handleBack = () => {
    navigateManageLearnerReportsBack(navigate, {
      from: navigationFrom,
      entityType: "simulation",
      entityId: id,
      fallback: userRoleService.canAccessCoachingReport()
        ? COACHING_REPORTS_PATH
        : undefined,
      selectedUserGroups,
      navigationState: location.state,
    });
  };
  
  return (
    <SimulationReportContext.Provider value={contextState}>
    <div
      className={cx(styles.reportsContainer, {
        [styles.sidebarCollapsed]: contextState.showCallRecording,
      })}
    >
      <div className={styles.header}>
        <div className={styles.backContainer}>
          <div
            className={styles.back}
            onClick={handleBack}
          >
            <i className="paperflite-left-arrow"></i>
            <span>{getManageReportsBackLabel("simulation")}</span>
          </div>
        </div>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.left}>
          <div className={styles.title}>
            completed learners ({totalRecordsCount || _.size(learners)})
          </div>
          <div className={styles.filtersRow}>
            <UserGroupsFilter
              selectedGroups={selectedUserGroups}
              onChange={handleUserGroupsChange}
            />
          </div>
          <div
            className={cx(styles.contentsList, {
              [styles.slideOut]: contextState.showCallRecording,
            })}
          >
            <InfiniteScroll
              className={styles.scroll}
              loading={learnersLoading}
              hasMore={hasMoreItems}
              loadMore={handleLoadMoreLearners}
              loader={() => {
                if (showReportsEmptyState) {
                  return null;
                }

                return _.times(
                  _.size(learners) === 0 || currentPage === 0 ? 7 : 1,
                  (index) => <LearnerPlaceholder key={index} />,
                );
              }}
            >
                {showReportsEmptyState && (
                  <div className={styles.emptyListState} role="status">
                    {_.times(3, (index) => (
                      <LearnerPlaceholder key={index} disableShimmer />
                    ))}
                  </div>
                )}

                {!showReportsEmptyState && (
                  <LearnersList selectedUserGroups={selectedUserGroups} />
                )}
            </InfiniteScroll>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.detailContainer}>
            {(loading || simulationLoading) &&
              !learner &&
              !showReportsEmptyState && <SimulationReportPlaceholder />}
            {showReportsEmptyState && (
              <div className={styles.emptyMessage} role="status">
                <h2 className={styles.emptyTitle}>no learners found</h2>
                <p className={styles.emptySubText}>{reportsEmptyDescription}</p>
              </div>
            )}
            {learner && <EditSimulationReportsDetail learner={learner} />}
          </div>
        </div>

        {contextState.showCallRecording && (
        <div className={styles.callRecordingWrapper}>
          <CallRecording learner={learner} />
        </div>
        )}
      </div>
    </div>
    </SimulationReportContext.Provider>
  );
};

export const LearnerPlaceholder: React.FC<{ disableShimmer?: boolean }> = ({ disableShimmer = false }) => {
  return (
    <div className={cx(styles.stacks, styles.placeholder)}>
      <div className={cx(styles.name, !disableShimmer && styles.block)}></div>
      <div className={styles.info}>
        <div className={cx(!disableShimmer && styles.block, styles.blockName)}></div>
        <div className={cx(!disableShimmer && styles.block, styles.blockDescription)}></div>
      </div>
    </div>
  );
};

export const SimulationReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.detailContainer, styles.placeholder)}>
      <div className={styles.headerContainer}>
        <div className={cx(styles.name, styles.block)}></div>
        <div className={styles.info}>
          <div className={cx(styles.title, styles.block)}></div>
          <div className={cx(styles.subTitle, styles.block)}></div>
      </div>
      </div>
      <div className={styles.content}>
        <div className={styles.card}></div>
        <div className={styles.card}></div>
        <div className={styles.card}></div>
      </div>
    </div>
  );
};